import { useLocation, useNavigate } from 'react-router-dom'
import InstagramReelCard from '@presentation/components/ui/InstagramReelCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useInstagramReels } from '@presentation/hooks/useInstagramReels'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import SeoHead from '@presentation/seo/SeoHead'

const DR_MICHAEL_OKUN_REELS_ROUTE = '/dr-michael-okun-my-take'

export default function InstagramReelsListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isReelsListRoute = location.pathname === DR_MICHAEL_OKUN_REELS_ROUTE
  useScrollToTopOnRouteEnter({ enabled: isReelsListRoute })
  const {
    reels,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    retry,
  } =
    useInstagramReels()

  const navigateBackToHome = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/', { replace: true })
  }

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      {isReelsListRoute ? (
        <SeoHead
          title="Prof Michael Okun: My Take | Parkinson's Insights"
          description="Explore recent Instagram posts and reels from Prof Michael Okun: My Take."
          canonicalPath={DR_MICHAEL_OKUN_REELS_ROUTE}
        />
      ) : null}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={navigateBackToHome}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light md:hidden"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="mt-5 ml-1 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
            Prof Michael Okun: My Take
          </h1>
        </div>

        {loading ? (
          <LoadingSpinner className="mt-8" />
        ) : error ? (
          <div className="mt-8 flex flex-col items-center gap-3">
            <p className="text-center text-text-secondary">
              Unable to load Instagram content. Please try again later.
            </p>
            <button
              type="button"
              onClick={() => {
                void retry()
              }}
              className="inline-flex rounded-lg bg-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
            >
              Retry
            </button>
          </div>
        ) : reels.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              No Instagram content yet
            </p>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {reels.map((reel) => (
                <InstagramReelCard key={reel.uid} reel={reel} />
              ))}
            </div>

            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    void loadMore()
                  }}
                  disabled={loadingMore}
                  className="inline-flex rounded-lg bg-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
